



















import { Component, Mixins, Watch } from 'vue-property-decorator'
import { FormStructure } from '@master/model/structure/FormStructure'
import { BasicProductV2 as BasicProduct } from '@master/model/kyc-form/product/BasicProductV2'
import TheRcmdProductTemplate from '../basicRecommendation/TheRcmdProductTemplate.vue'
import TheRcmdProductMixin from '../basicRecommendation/TheRcmdProductMixin'
import InvestmentProduct from '@master/model/kyc-form/product/InvestmentProduct'
import { TransactionTypeOptions } from '@master/model/options/TransactionTypeOptions'
import { WrapOptions } from '@master/model/options/WrapOptions'
import { isEmpty, uniq } from 'lodash/fp'

@Component({
  components: {
    TheRcmdProductTemplate
  }
})
export default class TheInvestmentProductModal extends Mixins(TheRcmdProductMixin) {
  // if there is needed on additional button, overwrite it in src
  get customProductStructure (): FormStructure<InvestmentProduct> {
    return null
  }

  addProductFund () {
    if (!this.mainBasicProduct.oid) {
      return this.uiService.alert.show({
        type: 'error',
        message: 'Please select a main investment product before adding funds.'
      })
    }
    const newProductFund = new InvestmentProduct(this.riskRatingService, this.mainBasicProduct)
    this.mainBasicProduct.investmentFunds.push(newProductFund)
  }

  deleteProductFund (event) {
    const { index } = event
    this.mainBasicProduct.investmentFunds.splice(index, 1)
  }

  get productFormStructure (): FormStructure<BasicProduct> {
    return [
      {
        label: 'Issuer',
        key: 'issuerOid',
        classes: ['colspan'],
        options: this.issuerWithMainInvestmentProducts,
        onChange: (oldVal, newVal) => {
          this.handleIssuerOnChange(newVal)
        }
      },
      {
        label: 'Account Name',
        key: 'oid',
        classes: ['colspan'],
        options: this.investmentMainProductDisplayList.map(prod => {
          return { label: prod.name, value: prod.oid }
        }),
        onChange: (oldVal, newVal) => {
          this.updateSelectedProduct(newVal, this.investmentMainProductDisplayList, this.mainBasicProduct)
        }
      },
      {
        label: 'Main Account Holder',
        key: 'clientIndex',
        classes: ['colspan'],
        options: this.ownerOptions.map(owner => {
          return {
            label: owner.name,
            value: owner.value
          }
        })
      },
      {
        label: 'Joint Account Holder',
        key: 'jointClientIndex',
        classes: ['colspan'],
        options: this.ownerOptions.filter(owner => owner.value !== this.mainBasicProduct.clientIndex).map(owner => {
          return {
            label: owner.name,
            value: owner.value
          }
        })
      },
      {
        label: 'Account Number',
        key: 'accountNumber',
        classes: ['colspan']
      }
    ]
  }

  get issuerWithMainInvestmentProducts () {
    return this.cdMetaList.retrieveIssuerFromProducts(this.productList.retrieveMainInvestmentProductList([])).map(issuer => {
      return { label: issuer.companyShortName, value: issuer.oid }
    })
  }

  get investmentMainProductDisplayList () {
    return this.productList.retrieveMainInvestmentProductList([this.mainBasicProduct.issuerOid])
  }

  getProductFundFormStructure (productFund: InvestmentProduct): FormStructure<InvestmentProduct> {
    const fundList = this.getFundList(productFund)
    return [
      {
        label: 'Priority',
        key: 'priority',
        options: this.priorityOptionsMap,
        onChange: (oldVal, newVal) => {
          this.handlePriorityOnChange(productFund, newVal)
        }
      },
      {
        label: 'Filtered by Need',
        key: 'need',
        options: this.needsOptionsMap,
        onChange: (oldVal, newVal) => {
          this.handleNeedsOnChange(productFund, newVal)
        }
      },
      {
        label: 'Fund Name',
        key: 'oid',
        options: fundList.map(prod => {
          return { label: prod.name, value: prod.oid }
        }),
        onChange: (oldVal, newVal) => {
          this.updateSelectedProduct(newVal, fundList, productFund)
        }
      },
      {
        label: 'Type of Transactions',
        key: 'transactionType',
        options: this.transactionTypeOptions
      },
      {
        label: 'Risk Rating',
        key: 'recommendedRiskProfileString',
        classes: ['colspan'],
        disabled: true
      },
      {
        label: 'Investment Amount ' + this.currencySymbol,
        key: 'sumAssured',
        classes: [],
        type: 'currency',
        precision: 2
      },
      {
        label: 'Payment Mode',
        key: 'paymentMode',
        classes: [],
        options: this.paymentModeOptions
      },
      {
        label: 'Frequency',
        key: 'frequency',
        classes: [],
        options: this.frequencyOptions
      },
      {
        label: 'Wrap / Non-Wrap',
        key: 'wrap',
        options: this.wrapOptions
      }
    ]
  }

  get wrapOptions () {
    return [{ label: '', value: '' }, ...WrapOptions]
  }

  get transactionTypeOptions () {
    return [{ label: undefined, value: undefined }, ...TransactionTypeOptions]
  }

  getFundList (productFund) {
    return this.productList.retrieveInvestmentFundsVia(this.mainBasicProduct?.productFamilyCode, this.mainBasicProduct.issuerOid, [productFund.need])
  }

  handleIssuerOnChange (newVal: string) {
    if (!newVal) {
      return
    }
    this.updateInsurerName(newVal)
    // had to update rider issuer name too
    // clear the product as issuer has change
    this.clearProductOidOnChange(this.mainBasicProduct)
  }

  updateInsurerName (issuerOid: string) {
    const issuerName = this.issuerWithMainInvestmentProducts.find(issuer => issuer.value === issuerOid).label
    this.mainBasicProduct.issuer = issuerName

    if (this.mainBasicProduct.investmentFunds?.length > 0) {
      this.mainBasicProduct.investmentFunds.forEach(funds => {
        funds.issuer = issuerName
        funds.issuerOid = issuerOid
      })
    }
  }

  clearProductOidOnChange (model) {
    model.resetProduct()
    // remember to clear the riders too
    if (model.investmentFunds?.length > 0) {
      model.investmentFunds.forEach(funds => funds.resetProduct())
    }
  }

  mounted () {
    this.updateSelectedProduct(this.mainBasicProduct.oid, this.investmentMainProductDisplayList, this.mainBasicProduct, false)
  }

  @Watch('saveOnClick')
  async saveProduct () {
    if (this.saveOnClick) {
      const mustFilledKey = [{ key: 'issuerOid', display: 'Issuer' }, { key: 'oid', display: 'Account Name' },
        { key: 'clientIndex', display: 'Main Account Holder' }]
      const mainAccountHasEmptyField = this.getProductNotFilledMessage(this.mainBasicProduct, mustFilledKey)

      if (mainAccountHasEmptyField?.length > 0) {
        await this.uiService.alert.show({
          type: 'error',
          message: mainAccountHasEmptyField.join(', ') + ' cannot be empty.'
        })
        this.$emit('save', { validationPass: false, investmentModal: undefined })
        return
      }

      // Check if there are existing rcmd that has the same account number
      if (!isEmpty(this.mainBasicProduct.accountNumber)) {
        const length = this.currentRcmdList.filter(rcmd => rcmd.accountNumber === this.mainBasicProduct.accountNumber && rcmd.referenceId !== this.mainBasicProduct.referenceId).length
        if (length > 0) {
          await this.uiService.alert.show({
            type: 'error',
            message: 'You already have an existing Investment Account with the same account number in the list.'
          })
          this.$emit('save', { validationPass: false, investmentModal: undefined })
          return
        }
      }

      if (this.mainBasicProduct.investmentFunds?.length > 0) {
      // clientIndex, priority
        const hasNotFilled = this.mainBasicProduct.investmentFunds.map(subFunds => {
          const mustFilledKey = [{ key: 'priority', display: 'Priority' }]
          if (subFunds.isCustom) {
            mustFilledKey.push({ key: 'productName', display: 'Custom Product Fund Name' })
            mustFilledKey.push({ key: 'recommendedRiskProfile', display: 'Custom Product Risk Rating' })
          }
          return this.getProductNotFilledMessage(subFunds, mustFilledKey)
        })
          .filter(data => data)
          .flat()
        if (hasNotFilled?.length > 0) {
          await this.uiService.alert.show({
            type: 'error',
            message: 'Investment Funds\'s ' + uniq(hasNotFilled).join(', ') + ' cannot be empty.'
          })
          this.$emit('save', { validationPass: false, investmentModal: undefined })
          return
        }
      }
      this.$emit('save', { validationPass: true, investmentModal: this.mainBasicProduct })
    }
  }
}
